import { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useEffectiveRole } from "../../../lib/roles";
import { useOnboarding } from "../lib/OnboardingProvider";
import { useDialogA11y } from "../lib/useDialogA11y";

export function CompletionSection() {
  const role = useEffectiveRole();
  const { state, refresh, completeOnboarding, completePending } = useOnboarding();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmError, setConfirmError] = useState<string | null>(null);
  const dialogRef = useRef<HTMLDivElement | null>(null);

  // Dialog a11y hook — called unconditionally to respect the Rules of
  // Hooks. It's a no-op unless the dialog is actually open.
  const closeConfirm = () => {
    setConfirmOpen(false);
    setConfirmError(null);
  };
  useDialogA11y(confirmOpen, dialogRef, closeConfirm);

  const isOwner = role === "owner_admin" || role === "super_admin";

  if (!state) return null;

  // Completed screen — visible for every authenticated role.
  if (state.overall_status === "completed") {
    const allComplete = state.items.every((i) => i.status === "complete");
    return (
      <section data-testid="onboarding-completed-screen" className="rounded-lg border border-emerald-200 bg-emerald-50 p-5 dark:border-emerald-900 dark:bg-emerald-950">
        <h2 className="text-lg font-semibold text-emerald-900 dark:text-emerald-100">Setup complete</h2>
        <dl className="mt-3 grid grid-cols-1 gap-2 text-sm text-emerald-900 dark:text-emerald-100 sm:grid-cols-2">
          <div><dt className="text-xs opacity-70">Completed at</dt><dd data-testid="completed-at">{state.completed_at || "—"}</dd></div>
          <div><dt className="text-xs opacity-70">Completed by</dt><dd data-testid="completed-by">{state.completed_by || "—"}</dd></div>
          <div><dt className="text-xs opacity-70">Selected template</dt><dd data-testid="completed-template">{state.selected_template_key || "—"}</dd></div>
          <div><dt className="text-xs opacity-70">Template version</dt><dd data-testid="completed-template-version">{state.selected_template_version || "—"}</dd></div>
        </dl>
        {!allComplete && (
          <p role="status" data-testid="completed-with-warning" className="mt-3 text-sm text-amber-800">
            Onboarding is complete, but part of the current configuration now needs attention. Review the checklist for details.
          </p>
        )}
        <div className="mt-4 flex gap-3 text-sm">
          <Link to="/" className="underline" data-testid="completed-dashboard-link">Return to dashboard</Link>
          <Link to="/onboarding?panel=tenant-profile" className="underline" data-testid="completed-review-link">Review onboarding</Link>
        </div>
      </section>
    );
  }

  if (!isOwner) return null;
  const ready = state.overall_status === "ready_to_complete";
  if (!ready) return null;

  const onConfirm = async () => {
    setConfirmError(null);
    await refresh();
    const result = await completeOnboarding();
    if (!result.ok) {
      const detail = result.detail as { code?: string; readiness_missing?: string[] } | null;
      if (detail?.code === "incomplete_steps") {
        setConfirmError("Some items became incomplete. The checklist has been refreshed.");
      } else {
        setConfirmError("Unable to complete onboarding. Please try again.");
      }
    } else {
      setConfirmOpen(false);
    }
  };

  return (
    <section data-testid="completion-section" className="rounded-lg border border-emerald-200 bg-emerald-50 p-5 dark:border-emerald-900 dark:bg-emerald-950">
      <h2 className="text-lg font-semibold">Ready to complete</h2>
      <p className="mt-2 text-sm">Every readiness item is complete. You can now finalize onboarding.</p>
      <button type="button" data-testid="onboarding-complete-open"
        onClick={() => { setConfirmError(null); setConfirmOpen(true); }}
        className="mt-3 rounded-md bg-slate-900 px-4 py-2 text-sm font-medium text-white dark:bg-slate-100 dark:text-slate-900">
        Complete onboarding
      </button>
      {confirmOpen && (
        <div role="dialog" aria-modal="true" aria-labelledby="complete-heading"
          data-testid="onboarding-complete-confirm-dialog"
          ref={dialogRef}
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="w-full max-w-md rounded-lg bg-white p-5 dark:bg-slate-900">
            <h3 id="complete-heading" className="text-base font-semibold">Complete onboarding</h3>
            <p className="mt-2 text-sm">
              This will mark tenant setup as complete. Milestone E does not support reopening.
            </p>
            {confirmError && (
              <p role="alert" data-testid="onboarding-complete-error" className="mt-3 text-sm text-amber-800">
                {confirmError}
              </p>
            )}
            <div className="mt-4 flex justify-end gap-2">
              <button type="button" onClick={closeConfirm}
                data-testid="onboarding-complete-cancel"
                className="rounded-md border px-3 py-1.5 text-sm dark:border-slate-700">Cancel</button>
              <button type="button" disabled={completePending}
                onClick={onConfirm}
                data-testid="onboarding-complete-confirm"
                className="rounded-md bg-slate-900 px-3 py-1.5 text-sm font-medium text-white disabled:opacity-50 dark:bg-slate-100 dark:text-slate-900">
                {completePending ? "Completing…" : "Confirm"}
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
